import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const empty = {
  name: "",
  slug: "",
  category: "3-5KW",
  short_description: "",
  description: "",
  image_url: "",
  features_text: "",
  specs_text: "",
  is_featured: false,
  order: 0,
};

const CATEGORIES = ["1-3KW", "3-5KW", "5-10KW", "Yüksek Güç", "Yarış"];

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing("new");
    setForm(empty);
  };

  const openEdit = (p) => {
    setEditing(p.id);
    setForm({
      ...p,
      features_text: (p.features || []).join("\n"),
      specs_text: Object.entries(p.specs || {}).map(([k, v]) => `${k}: ${v}`).join("\n"),
    });
  };

  const closeModal = () => { setEditing(null); setForm(empty); };

  const update = (k) => (e) => setForm({ ...form, [k]: e.target?.type === "checkbox" ? e.target.checked : e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setLoading(true);
    const features = form.features_text.split("\n").map((s) => s.trim()).filter(Boolean);
    const specs = {};
    form.specs_text.split("\n").forEach((line) => {
      const [k, ...rest] = line.split(":");
      if (k && rest.length > 0) specs[k.trim()] = rest.join(":").trim();
    });
    const payload = {
      name: form.name,
      slug: form.slug,
      category: form.category,
      short_description: form.short_description,
      description: form.description,
      image_url: form.image_url,
      features,
      specs,
      is_featured: !!form.is_featured,
      order: Number(form.order) || 0,
    };
    try {
      if (editing === "new") {
        await api.post("/admin/products", payload);
        toast.success("Ürün eklendi");
      } else {
        await api.put(`/admin/products/${editing}`, payload);
        toast.success("Ürün güncellendi");
      }
      await load();
      closeModal();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Kaydedilemedi");
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Bu ürünü silmek istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Ürün silindi");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Silinemedi");
    }
  };

  return (
    <div className="p-8" data-testid="admin-products-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#059669] mb-2">Yönetim</p>
          <h1 className="font-display text-3xl md:text-4xl tracking-tighter font-black">Ürünler</h1>
        </div>
        <button
          onClick={openNew}
          data-testid="add-product-btn"
          className="inline-flex items-center gap-2 bg-[#059669] text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-emerald-700"
        >
          <Plus size={14} /> Yeni Ürün
        </button>
      </div>

      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-[10px] uppercase tracking-[0.18em] text-gray-500">
              <th className="text-left px-4 py-3 font-semibold">Ürün</th>
              <th className="text-left px-4 py-3 font-semibold">Kategori</th>
              <th className="text-left px-4 py-3 font-semibold">Slug</th>
              <th className="text-left px-4 py-3 font-semibold">Sıra</th>
              <th className="text-left px-4 py-3 font-semibold">Öne Çıkan</th>
              <th className="text-right px-4 py-3 font-semibold">İşlem</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {products.map((p) => (
              <tr key={p.id} className="hover:bg-gray-50" data-testid={`admin-product-row-${p.slug}`}>
                <td className="px-4 py-3 font-display">{p.name}</td>
                <td className="px-4 py-3 text-gray-600">{p.category}</td>
                <td className="px-4 py-3 font-mono-fd text-xs text-gray-500">{p.slug}</td>
                <td className="px-4 py-3">{p.order}</td>
                <td className="px-4 py-3">
                  {p.is_featured ? (
                    <span className="text-[10px] uppercase tracking-[0.18em] bg-emerald-50 text-emerald-700 px-2 py-1">Evet</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.18em] bg-gray-100 text-gray-500 px-2 py-1">Hayır</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(p)} data-testid={`edit-${p.slug}`} className="inline-flex items-center gap-1 text-gray-600 hover:text-[#059669] mr-3">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => remove(p.id)} data-testid={`delete-${p.slug}`} className="inline-flex items-center gap-1 text-gray-600 hover:text-red-600">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {products.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-400">Henüz ürün yok</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4 overflow-auto" data-testid="product-modal">
          <form onSubmit={save} className="bg-white border border-gray-200 w-full max-w-3xl my-8">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <h2 className="font-display text-xl tracking-tight">{editing === "new" ? "Yeni Ürün" : "Ürünü Düzenle"}</h2>
              <button type="button" onClick={closeModal} className="text-gray-500 hover:text-black"><X size={18} /></button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Ürün Adı *" testid="form-name" value={form.name} onChange={update("name")} required />
              <Field label="Slug *" testid="form-slug" value={form.slug} onChange={update("slug")} required placeholder="nd72350" />
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-gray-600 mb-2">Kategori</label>
                <select value={form.category} onChange={update("category")} className="w-full bg-gray-50 border border-gray-200 px-3 py-2.5 text-sm">
                  {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                </select>
              </div>
              <Field label="Sıra" type="number" value={form.order} onChange={update("order")} />
              <div className="md:col-span-2">
                <Field label="Kısa Açıklama" value={form.short_description} onChange={update("short_description")} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-[0.18em] text-gray-600 mb-2">Açıklama</label>
                <textarea rows={4} value={form.description} onChange={update("description")} className="w-full bg-gray-50 border border-gray-200 px-3 py-2.5 text-sm resize-none" />
              </div>
              <div className="md:col-span-2">
                <Field label="Görsel URL" value={form.image_url} onChange={update("image_url")} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-[0.18em] text-gray-600 mb-2">Özellikler (her satıra bir madde)</label>
                <textarea rows={4} value={form.features_text} onChange={update("features_text")} className="w-full bg-gray-50 border border-gray-200 px-3 py-2.5 text-sm font-mono-fd resize-none" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs uppercase tracking-[0.18em] text-gray-600 mb-2">Teknik Spec (Anahtar: Değer formatında, her satıra bir tane)</label>
                <textarea rows={5} value={form.specs_text} onChange={update("specs_text")} placeholder="Voltaj: 72V&#10;Pik Akım: 350A" className="w-full bg-gray-50 border border-gray-200 px-3 py-2.5 text-sm font-mono-fd resize-none" />
              </div>
              <label className="flex items-center gap-2 md:col-span-2 text-sm">
                <input type="checkbox" checked={!!form.is_featured} onChange={update("is_featured")} />
                Öne çıkan ürün olarak işaretle
              </label>
            </div>
            <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
              <button type="button" onClick={closeModal} className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-gray-300 hover:bg-gray-50">İptal</button>
              <button type="submit" disabled={loading} data-testid="save-product-btn" className="px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] bg-[#059669] text-white hover:bg-emerald-700 disabled:opacity-50">
                {loading ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-gray-600 mb-2">{label}</label>
      <input data-testid={testid} {...props} className="w-full bg-gray-50 border border-gray-200 px-3 py-2.5 text-sm" />
    </div>
  );
}
